import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import classNames from 'classnames';
import { useIntl, IntlProvider } from 'react-intl';
import gql from 'graphql-tag';
import find from 'lodash/find';
import get from 'lodash/get';
import sumBy from 'lodash/sumBy';
import startCase from 'lodash/startCase';
import parseISO from 'date-fns/parseISO';
import styles from './styles.scss';
import Translation from '../../../../components/stateless/Translation';
import LinkOut from '../../../../components/stateless/LinkOut';
import renderAmenities from '../../../../components/stateful/UnitsTable/themes/StorageMart/renderAmenities';
import useLazyQuery from '../../../../../modules/react/hooks/useLazyQuery';
import { getQueryParams } from '../../../../../modules/search';
import LoadingIndicator from '../../../../components/stateless/LoadingIndicator';
import Date from '../../../../components/stateless/Date';
import SMFormFeedback from '../../../components/stateless/SMFormFeedback';
import {
  TRACK_CLICK_CHECKOUT_GET_DIRECTIONS,
  TRACK_CLICK_CHECKOUT_PRINT_ORDER,
  TRACK_CHECKOUT_STEP,
  TRACK_CHECKOUT_PURCHASE,
} from '../../../../../src/web/utils/dataLayer';
import GET_FACILITY_QUERY from './fragments/GetFacility';
import Price from '../../../../components/stateless/Price';
import SMTooltip from '../../../components/stateless/SMTooltip';
import LeaseSignature from './LeaseSignature';
import { sizeMessageProps, RENT_MONTH_NAMES, VALUE_COVERAGE_MONTH_NAMES } from '../StorageMartCheckoutStoredge/constants';
import intlPath from '../../../../../src/web/utils/intlPath';

const rentItemRegex = /rent unit/i;
const valueCoverageRegex = /protection plan/i;
const adminFeeRegex = /administrative fee/i;
const lockRegex = /lock/i;
const dateRegex = /[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{4}/;
const webOnlyText = '[WEB ONLY] ';

export const GET_FACILITY = gql`
  ${GET_FACILITY_QUERY}
`;

const propTypes = {
  isBuilderView: PropTypes.bool,
  loadingStorableConfirmation: PropTypes.bool.isRequired,
  storableConfirmationData: PropTypes.shapeOf({}),
  storableConfirmationError: PropTypes.string,
};

const defaultProps = {
  isBuilderView: false,
  storableConfirmationData: undefined,
  storableConfirmationError: undefined,
};

const StorageMartCheckoutConfirmation = ({
  isBuilderView,
  isReservation,
  loadingStorableConfirmation,
  storableConfirmationData,
  storableConfirmationError,
  storableCheckoutConfirmation,
  loadingStorableLedger,
  storableLedgerData,
  storableLedgerError,
  getLedgerWithAddons,
  getLeadData,
  leadLoading,
  leadData,
  leadError,
  esignPath,
}) => {
  const intl = useIntl();
  const { locale, formatMessage, messages } = intl;
  const {
    confirmationId,
    facilityId,
    leadId,
  } = getQueryParams();
  const signInUrl = `https://my-account.storage-mart.com?lang=${locale}`;

  const [facilityQuery, {
    loading: facilityLoading,
    error: facilityError,
    data: facilityData,
  }] = useLazyQuery(GET_FACILITY, {
    variables: { id: facilityId },
  });

  useEffect(() => {
    if (facilityId && !facilityData && !isBuilderView) {
      facilityQuery();
    }
  }, [facilityId]);

  useEffect(() => {
    if (facilityId && confirmationId && !storableConfirmationData
      && !loadingStorableConfirmation && !isBuilderView
    ) {
      storableCheckoutConfirmation(facilityId, confirmationId);
    }

    if (facilityId && leadId && !leadData
      && !leadLoading && !isBuilderView
    ) {
      getLeadData({ facilityId, leadId });
    }
  }, [confirmationId, facilityId, leadId]);

  useEffect(() => {
    if (storableConfirmationData && !storableLedgerData) {
      getLedgerWithAddons(facilityId, storableConfirmationData.ledger_id);
    }
  }, [storableConfirmationData, storableLedgerData, facilityId]);

  useEffect(() => {
    if (get(storableLedgerData, 'success', false)
      && get(facilityData, 'facility')
      && get(storableConfirmationData, 'success')
      && get(storableLedgerData, 'success')
    ) {
      const checkoutUnit = get(storableLedgerData, 'unit');
      const checkoutFacility = get(facilityData, 'facility');
      const checkoutUnitGroup = find(get(facilityData, 'facility.unitGroups'), { id: checkoutUnit.unit_group_id });

      TRACK_CHECKOUT_STEP(checkoutUnitGroup, checkoutFacility, 'new_booking_flow_step5', 'thank you and e-signature');
      TRACK_CHECKOUT_PURCHASE({
        unitGroup: checkoutUnitGroup,
        facility: checkoutFacility,
        confirmationNumber: get(storableConfirmationData, 'id'),
        intl,
        confirmationUnit: checkoutUnit,
        discountPlan: get(storableLedgerData, 'discounts[0].name', null),
      });
    }
  }, [storableLedgerData, facilityData, storableConfirmationData, storableLedgerData]);

  if (storableConfirmationError || facilityError || storableLedgerError || leadError
    || (storableConfirmationData && !storableConfirmationData.success)
    || (storableLedgerData && !storableLedgerData.success)
  ) {
    return (
      <SMFormFeedback
        context="danger"
        id="widgets.checkoutconfirmation.loadingError"
        defaultMessage="There was an error loading the confirmation"
      />
    );
  }

  const loadingConfirmation = !isReservation && (
    !storableConfirmationData
    || loadingStorableConfirmation
    || !storableLedgerData
    || loadingStorableLedger
  );
  const loadingReservation = isReservation && (!leadData || leadLoading);
  if (!isBuilderView
    && (
      loadingConfirmation
      || loadingReservation
      || facilityLoading
    )
  ) {
    return (
      <div>
        <div style={{
          height: '100px',
          width: '100%',
          marginBottom: '1em',
          marginTop: '1em',
        }}
        >
          <LoadingIndicator />
        </div>
        <div className={styles.orderSummary}>
          <div className={classNames('row', styles.flex)}>
            <Col xs={12} md={8}>
              <div style={{ height: '450px', marginBottom: '1em' }}><LoadingIndicator /></div>
            </Col>
            <Col xs={12} md={4}>
              <div style={{ height: '450px' }}><LoadingIndicator /></div>
            </Col>
          </div>
        </div>
      </div>
    );
  }

  const {
    moved_in_at: movedInAt,
    desiredMoveInDate,
    autopay_info: {
      autopay_enabled: autopayEnabled,
    } = {},
    tenant: {
      first_name: firstName,
      last_name: lastName,
      email,
      phone_numbers: phoneNumbers,
      mailing_address: tenantAddress,
      contacts,
    },
    unit: {
      name: unitName,
      length,
      width,
      discountedPrice,
      discountedPriceWeekly,
      price,
      priceWeekly,
      unit_type: {
        name: unitTypeName,
      },
      unit_amenities: unitAmenities,
      unit_group_id: unitGroupId,
    },
  } = isBuilderView ? {
    ledgerId: '12345',
    movedInAt: '2022-09-12T22:36:28.446-10:00',
    desiredMoveInDate: '2022-09-12T22:36:28.446-10:00',
    autopay_info: {
      autopay_enabled: false,
    },
    tenant: {
      firstName: 'New',
      lastName: 'Tenant',
      email: 'tenantemail@email.com',
      phone_numbers: [
        {
          formatted_number: '(555) 123-1234',
          primary: true,
          sms_opt_in: true,
        },
      ],
      mailing_address: {
        address1: '1000 Main St',
        address2: 'Apt 2',
        city: 'Anytown',
        state: 'KS',
        postal: '55555',
      },
      contacts: [
        {
          first_name: 'John',
          last_name: 'Doe',
          primary: true,
          description: 'Emergency Contact',
          phone_numbers: [
            {
              formatted_number: '(555) 123-1234',
            },
          ],
        },
        {
          first_name: 'Jane',
          last_name: 'Doe',
          primary: false,
          description: 'Authorized User',
        },
      ],
    },
    unit: {
      id: '12345',
      name: '100',
      price: 100,
      length: '5',
      width: '10',
      height: '8',
      unit_type: {
        name: 'Small',
      },
      unit_amenities: [
        {
          name: 'Climate Controlled',
          token: 'climatecontrolled',
        },
      ],
    },
  } : storableLedgerData || leadData;

  const {
    facility: {
      currency,
      surchargePercentage,
      address: facilityAddress,
      phone: facilityPhone,
      settings: {
        useWeeklyRates,
      } = {},
      unitGroups,
    },
  } = isBuilderView ? {
    facility: {
      currency: 'USD',
      brandName: 'sm',
      address: {
        address1: '9999 Main Street',
        address2: '',
        city: 'Columbia',
        state: 'MO',
        postal: '65203',
        country: 'US',
      },
      phone: '(555) 555-5555',
      unitGroups: [
        {
          id: '123-456-789',
          category: 'small',
          categoryName: 'small',
          isParking: false,
          width: 10,
          length: 5,
          height: 8,
        },
      ],
    },
  } : facilityData;

  const {
    invoice_line_items: invoiceLineItems,
    successful_payment_events: successfulPaymentEvents,
    esign_url: storedgeEsignUrl,
    move_in_total: moveInTotal,
    move_in_total_with_surcharge: moveInTotalWithSurcharge,
    surcharge,
    applied_discount_plans: appliedDiscountPlans,
  } = isBuilderView ? {
    invoice_line_items: [{
      full_description: 'Rent #100 - 1/1/2000 - prorated',
      description: 'Rent #100',
      discount_amount: 45,
      subtotal: 45,
      tax_total: 10,
      total: 100,
    },
    {
      full_description: 'Admin Fee',
      description: 'Admin Fee',
      subtotal: 25,
      tax_total: 0,
      total: 25,
    }],
    successful_payment_events: [],
    esign_url: 'https://www.test.com/12345',
    applied_discount_plans: [
      {
        id: '123456789',
        name: '1STMONTHFREE',
        amount: 100,
      },
    ],
    surcharge: {
      fee: 2.5,
    },
    move_in_total: 100,
    move_in_total_with_surcharge: 102.5,
  } : storableConfirmationData || {};

  const invoiceTaxTotal = sumBy(invoiceLineItems, 'tax_total');

  const printDialog = () => (
    window.print()
  );

  const emergencyContact = find(contacts, { primary: true });
  const authorizedUser = find(contacts, { primary: false });
  const weeklyRate = (discountedPriceWeekly || priceWeekly);
  const monthlyRate = (discountedPrice || price);
  const unitPrice = useWeeklyRates ? weeklyRate : monthlyRate;
  const facilityUnitGroup = unitGroups.find(ug => ug.id === unitGroupId);
  const isParking = facilityUnitGroup?.isParking;

  const size = facilityUnitGroup?.isParking
    ? `${facilityUnitGroup.width}x${facilityUnitGroup.length}`
    : `${width}x${length}`;

  let paymentMethodType = null;
  let paymentMethodNumber = null;
  let paymentAmount = null;
  let showSurcharge = false;

  if (!isReservation && successfulPaymentEvents.length) {
    const paymentMethod = successfulPaymentEvents[0].payment_method || {};
    const {
      card_type: cardType,
      bank_name: bankName,
      account_type: accountType,
      card_number: cardNumber,
      account_number: accountNumber,
    } = paymentMethod;

    const hasCardType = !!cardType;
    const hasBankInfo = !!bankName || !!accountType;

    const paymentMethodBankInfo = hasBankInfo ? [bankName, accountType].filter(Boolean).join(' ') : '';
    const paymentMethodTypeRaw = hasCardType
      ? cardType
      : paymentMethodBankInfo;

    const normalizedPaymentMethodTypeRaw = typeof paymentMethodTypeRaw === 'string'
      ? paymentMethodTypeRaw.trim()
      : '';
    paymentMethodType = normalizedPaymentMethodTypeRaw
      ? startCase(normalizedPaymentMethodTypeRaw)
      : 'Other';

    if (cardNumber) {
      paymentMethodNumber = cardNumber;
    } else if (typeof accountNumber === 'string' && accountNumber.length >= 4) {
      paymentMethodNumber = accountNumber.substring(accountNumber.length - 4);
    } else {
      paymentMethodNumber = '';
    }
    paymentAmount = successfulPaymentEvents[0].amount;
    showSurcharge = moveInTotal !== moveInTotalWithSurcharge
      && paymentAmount === moveInTotalWithSurcharge;
  } else {
    paymentAmount = moveInTotal;
  }

  return (
    <div className={`${styles.checkoutConfirmation} pt-12`}>
      <div className={styles.header}>
        <div>
          {
            isReservation
              ? (
                <Row>
                  <Col xs={12}>
                    <div className="flex flex-col md:flex-row justify-between gap-2 mb-4">
                      <div>
                        <h1 className={styles.title}>
                          <Translation
                            id="widgets.checkoutconfirmation.storable.reservation.title"
                            defaultMessage="Thank you for your reservation."
                          />
                        </h1>
                        <p>
                          <Translation
                            id="widgets.checkoutconfirmation.storable.reservation.titleText1"
                            defaultMessage="We’ve emailed your order confirmation and next steps to complete your rental."
                          />
                        </p>
                      </div>
                      <div>
                        <a
                          className={`${styles.actionLink} ${styles.printOrder} bg-no-repeat bg-left leading-8`}
                          onClick={printDialog}
                          onKeyPress={printDialog}
                          role="button"
                          tabIndex="0"
                          {...TRACK_CLICK_CHECKOUT_PRINT_ORDER}
                        >
                          <Translation
                            id="widgets.checkoutConfirmation.storable.reservation.printOrder"
                            defaultMessage="Print my reservation"
                          />
                        </a>
                      </div>
                    </div>
                  </Col>
                </Row>
              )
              : (
                <>
                  <Row>
                    <Col xs={12}>
                      <div className="flex flex-col md:flex-row justify-between gap-2 mb-4">
                        <div>
                          <h1 className={styles.title}>
                            <Translation
                              id="widgets.checkoutconfirmation.storable.checkout.title"
                              defaultMessage="Thank you for your rental."
                            />
                          </h1>
                          <p>
                            <Translation
                              id="widgets.checkoutconfirmation.checkout.signin"
                              defaultMessage="sign in"
                            >
                              {linkText => (
                                <Translation
                                  id="widgets.checkoutconfirmation.storable.checkout.titleText1"
                                  defaultMessage="We’ve emailed your order confirmation and unit access instructions."
                                  values={{
                                    email,
                                    link: <LinkOut href={signInUrl}>{linkText}</LinkOut>,
                                  }}
                                />
                              )}
                            </Translation>
                          </p>
                        </div>
                        <div>
                          <a
                            className={`${styles.actionLink} ${styles.printOrder} bg-no-repeat bg-left leading-8`}
                            onClick={printDialog}
                            onKeyPress={printDialog}
                            role="button"
                            tabIndex="0"
                            {...TRACK_CLICK_CHECKOUT_PRINT_ORDER}
                          >
                            <Translation
                              id="widgets.checkoutConfirmation.storable.printOrder"
                              defaultMessage="Print my order"
                            />
                          </a>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12}>
                      <Row>
                        <Col xs={12}>
                          <LeaseSignature
                            esignPath={esignPath}
                            storedgeEsignUrl={storedgeEsignUrl}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </>
              )
          }
        </div>
      </div>
      <div className={styles.orderSummary}>
        <div className={classNames('row', styles.flex)}>
          <Col xs={12} md={8}>
            <div className={styles.content}>
              <Row>
                <Col xs={12}>
                  <h2 className="pb-4 px-4 -mx-4 mb-5 border-b border-gray-300">
                    {isReservation ? (
                      <Translation
                        id="widgets.checkoutConfirmation.storable.reservation.leftContent.title"
                        defaultMessage="Your Reservation Summary"
                      />
                    ) : (
                      <Translation
                        id="widgets.checkoutconfirmation.storable.leftContent.title"
                        defaultMessage="Your Rental Summary"
                      />
                    )}
                  </h2>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <div className="mb-4">
                    <h5>
                      <Translation
                        id="widgets.checkoutconfirmation.storable.unitDetails.block3Title"
                        defaultMessage="Location"
                      />
                    </h5>
                    <div>{facilityAddress.address1}</div>
                    <div>{facilityAddress.address2}</div>
                    <div>{`${facilityAddress.city}, ${facilityAddress.state} ${facilityAddress.postal}`}</div>
                    <div>{facilityPhone}</div>

                    <a
                      className={`${styles.actionLink} ${styles.getDirections} bg-no-repeat`}
                      href={`https://maps.google.com/?q=${facilityAddress.address1}, ${facilityAddress.address2}, ${facilityAddress.city}, ${facilityAddress.state} ${facilityAddress.postal}`}
                      target="_blank"
                      rel="noreferrer"
                      {...TRACK_CLICK_CHECKOUT_GET_DIRECTIONS}
                    >
                      <Translation
                        id="widgets.checkoutConfirmation.storable.getDirections"
                        defaultMessage="Get Directions"
                      />
                    </a>
                  </div>
                  <div className="mb-4">
                    <h5 className="mb-4">
                      <Translation
                        id={`widgets.checkoutconfirmation.${isReservation ? 'reservation' : 'checkout'}.rightContent.title`}
                        defaultMessage={isReservation ? 'Contact and Payment Information' : 'Personal and Billing Information'}
                      />
                    </h5>
                    <div className="mb-4">
                      <div>
                        {firstName}
                        {' '}
                        {lastName}
                      </div>
                      <div>{tenantAddress.address1}</div>
                      <div>{tenantAddress.address2}</div>
                      <div>{`${tenantAddress.city}, ${tenantAddress.state} ${tenantAddress.postal}`}</div>
                      <div>{email}</div>
                      <div>{get(phoneNumbers, '[0].formatted_number')}</div>
                    </div>
                    {
                      emergencyContact && (
                        <div className="mb-4">
                          <h5>
                            <Translation
                              id="widgets.checkoutconfirmation.storable.rightContent.block2Title"
                              defaultMessage="Emergency Contact"
                            />
                          </h5>
                          <div>
                            {emergencyContact.first_name}
                            {' '}
                            {emergencyContact.last_name}
                          </div>
                          <div>{get(emergencyContact, 'phone_numbers[0].formatted_number')}</div>
                        </div>
                      )
                    }
                    {
                      authorizedUser && (
                        <div className="mb-4">
                          <h5>
                            <Translation
                              id="widgets.checkoutconfirmation.storable.rightContent.block3Title"
                              defaultMessage="Authorized to Access"
                            />
                          </h5>
                          <div>
                            {authorizedUser.first_name}
                            {' '}
                            {authorizedUser.last_name}
                          </div>
                        </div>
                      )
                    }
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="mb-4">
                    <h5>
                      <Translation
                        id="widgets.checkoutconfirmation.storable.leftContent.block1Title"
                        defaultMessage="Unit"
                      />
                      {' '}
                      {`#${unitName}`}
                    </h5>
                    <div>
                      <Price
                        currency={currency}
                        locale={locale}
                        value={unitPrice}
                      />
                      <Translation
                        id={useWeeklyRates ? 'widgets.abbreviation.wk' : 'widgets.abbreviation.mo'}
                        defaultMessage={useWeeklyRates ? '/wk' : '/mo'}
                      />
                    </div>
                    <div>
                      {
                        isReservation
                          ? (
                            <Translation
                              id="widgets.checkoutconfirmation.storable.reservation.unitDetails.checkInDate"
                              defaultMessage="Unit Reserved Until: {checkInDate}"
                              values={{
                                checkInDate: (
                                  <Date
                                    day="2-digit"
                                    month="long"
                                    value={parseISO(movedInAt || desiredMoveInDate)}
                                    year="numeric"
                                  />
                                ),
                              }}
                            />
                          )
                          : (
                            <>
                              <Translation
                                id="widgets.checkoutconfirmation.storable.unitDetails.checkInDate"
                                defaultMessage="Check-in Date:"
                              />
                              {' '}
                              <Date
                                day="2-digit"
                                month="long"
                                value={parseISO(movedInAt || desiredMoveInDate)}
                                year="numeric"
                              />
                            </>
                          )
                      }
                    </div>
                  </div>
                  <div className="mb-4">
                    <h5>
                      {size}
                      {' '}
                      {facilityUnitGroup ? (
                        <Translation
                          {...sizeMessageProps[facilityUnitGroup.categoryName.toLowerCase()]}
                        />
                      ) : unitTypeName}
                    </h5>
                    {renderAmenities(unitAmenities, styles.amenity, styles.tooltipIcon)}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className={styles.content}>
              <Row>
                <Col xs={12}>
                  <h2 className="pb-4 px-4 -mx-4 mb-5 border-b border-gray-300">
                    <Translation
                      id="widgets.checkoutconfirmation.storable.orderSummary.title"
                      defaultMessage="Order Summary"
                    />
                  </h2>
                </Col>
              </Row>
              <div className="flex flex-col">
                {!isReservation ? (
                  <>
                    {(() => {
                      const getDesc = item => item.full_description || item.fullDescription || '';
                      const getItemPrice = item => item.subtotal ?? item.amount ?? 0;

                      const getDisplayDescription = (item) => {
                        const desc = getDesc(item);
                        const dateResult = dateRegex.exec(desc);
                        let label = (item.description || '').replace(webOnlyText, '');
                        if (dateResult) {
                          const [month] = dateResult[0].split('/');
                          const isValueCoverage = valueCoverageRegex.test(desc);
                          label = formatMessage(isValueCoverage
                            ? VALUE_COVERAGE_MONTH_NAMES[month]
                            : RENT_MONTH_NAMES[month]);
                        }
                        return label;
                      };

                      const rentItem = invoiceLineItems.find(item => (
                        rentItemRegex.test(getDesc(item))
                      ));
                      const lockItem = invoiceLineItems.find(item => lockRegex.test(getDesc(item)));
                      const adminItem = invoiceLineItems.find(item => (
                        adminFeeRegex.test(getDesc(item))
                      ));
                      const otherItems = invoiceLineItems.filter((item) => {
                        const desc = getDesc(item);
                        return !rentItemRegex.test(desc)
                          && !lockRegex.test(desc)
                          && !adminFeeRegex.test(desc);
                      });

                      const rentDiscountAmount = rentItem
                        ? (rentItem.discount_amount ?? rentItem.discountAmount ?? 0)
                        : 0;
                      const rentDisplayAmount = rentItem
                        ? getItemPrice(rentItem) + rentDiscountAmount
                        : 0;
                      const feeItems = [lockItem, adminItem].filter(Boolean);
                      const showSurchargeAmount = surcharge?.fee > 0;
                      const showSurchargeDiscount = showSurchargeAmount && !showSurcharge;

                      return (
                        <>
                          {rentItem && (
                            <div className="flex justify-between mb-3">
                              <div>{getDisplayDescription(rentItem)}</div>
                              <div>
                                <Price
                                  currency={currency}
                                  locale={locale}
                                  value={rentDisplayAmount}
                                />
                              </div>
                            </div>
                          )}

                          {appliedDiscountPlans?.filter(p => Math.abs(p.amount) > 0).map(plan => (
                            <div key={plan.id || plan.name} className="flex justify-between mb-3">
                              <div className="flex items-center gap-1 text-[#2a8703]">
                                <i className="fa fa-tag" aria-hidden="true" />
                                {/* eslint-disable-next-line prefer-template */}
                                <Translation id={'widgets.unitstablev2.storageMart.unitPromo.' + plan.name} />
                              </div>
                              <div className="bg-[#e8f5e9] text-[#2a8703] px-2 py-0.5 rounded font-medium whitespace-nowrap self-center">
                                -<Price
                                  currency={currency}
                                  locale={locale}
                                  value={Math.abs(plan.amount)}
                                />
                              </div>
                            </div>
                          ))}

                          {feeItems.length > 0 && (
                            <div className="mb-3">
                              <div className="mb-2">
                                <Translation id="widgets.moveInCostBreakdown.oneTimeFees" defaultMessage="One-time Fees" />
                              </div>
                              <div className="border-l-2 border-gray-300 pl-3 ml-1 flex flex-col gap-2">
                                {lockItem && (
                                  <div className="flex justify-between">
                                    <div className="flex gap-2">
                                      <Translation id="widgets.moveInCostBreakdown.lockFee" defaultMessage="Lock" />
                                      <SMTooltip
                                        body={(
                                          <IntlProvider locale={locale} messages={messages}>
                                            <Translation
                                              id="widgets.checkout.summary.lockDisclaimer"
                                              defaultMessage="You may return the unused lock"
                                            />
                                          </IntlProvider>
                                        )}
                                      />
                                    </div>
                                    <div>
                                      <Price
                                        currency={currency}
                                        locale={locale}
                                        value={getItemPrice(lockItem)}
                                      />
                                    </div>
                                  </div>
                                )}
                                {adminItem && (
                                  <div className="flex justify-between">
                                    <div><Translation id="widgets.moveInCostBreakdown.adminFee" defaultMessage="Admin" /></div>
                                    <div>
                                      <Price
                                        currency={currency}
                                        locale={locale}
                                        value={getItemPrice(adminItem)}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {otherItems.map(item => (
                            <div key={getDesc(item)} className="flex justify-between mb-3">
                              <div>
                                {getDisplayDescription(item)}
                                {item.quantity > 1 ? ` (${item.quantity})` : null}
                              </div>
                              <div>
                                <Price
                                  currency={currency}
                                  locale={locale}
                                  value={getItemPrice(item)}
                                />
                              </div>
                            </div>
                          ))}

                          {invoiceTaxTotal > 0 && (
                            <div className="flex justify-between mb-3">
                              <div><Translation id="widgets.checkout.summary.taxes" defaultMessage="Taxes" /></div>
                              <div>
                                <Price
                                  currency={currency}
                                  locale={locale}
                                  value={invoiceTaxTotal}
                                />
                              </div>
                            </div>
                          )}

                          {showSurchargeAmount && (
                            <div className="flex justify-between mb-3">
                              <div>
                                <Translation
                                  id="widgets.moveInCostBreakdown.processingFee"
                                  defaultMessage="{surchargePercentage}% Processing Fee"
                                  values={{ surchargePercentage }}
                                />
                              </div>
                              <div>
                                <Price
                                  currency={currency}
                                  locale={locale}
                                  value={surcharge.fee}
                                />
                              </div>
                            </div>
                          )}

                          {showSurchargeDiscount && (
                            <div className="flex justify-between mb-3">
                              <div className="text-[#2a8703]">
                                <Translation
                                  id="widgets.checkout.summary.processingFeeDiscount"
                                  defaultMessage="Processing Fee Discount"
                                />
                              </div>
                              <div className="bg-[#e8f5e9] text-[#2a8703] px-2 py-0.5 rounded font-medium whitespace-nowrap self-center">
                                -<Price
                                  currency={currency}
                                  locale={locale}
                                  value={surcharge.fee}
                                />
                              </div>
                            </div>
                          )}

                          <hr className="my-3 border-gray-300" />
                          <div className="flex justify-between font-bold mb-3">
                            <div>
                              <Translation
                                id="widgets.checkout.summary.firsttotaldue"
                                defaultMessage="1st Month Total"
                              />
                            </div>
                            <div>
                              <Price
                                currency={currency}
                                locale={locale}
                                value={paymentAmount}
                              />
                              {!isParking && <sup>*</sup>}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </>
                ) : null}
                {
                  !isReservation && successfulPaymentEvents.length ? (
                    <>
                      <div className="mb-4 mt-4">
                        <h5>
                          <Translation
                            id="widgets.checkoutconfirmation.storable.rightContent.block5Title"
                            defaultMessage="Paid with {cardType} ending with {lastFour}"
                            values={{
                              cardType: paymentMethodType,
                              lastFour: paymentMethodNumber,
                            }}
                          />
                        </h5>
                        {
                          autopayEnabled && (
                            <div>
                              <Translation
                                id="widgets.checkoutconfirmation.storable.rightContent.authorizedAutopay"
                                defaultMessage="Monthly autopay authorized"
                              />
                            </div>
                          )
                        }
                      </div>
                    </>
                  ) : null
                }
                {!isParking && (
                  <div className="text-xs text-[#565958]">
                    <sup>*</sup>
                    {' '}
                    <Translation
                      id="widgets.moveInCostBreakdown.footnoteTotal"
                      defaultMessage="Rate includes up to two free months of {valueCoverage}."
                      values={{
                        valueCoverage: (
                          <a href={intlPath(locale, '/about-us/value-coverage')} className="!text-[#007a7c] underline">
                            <Translation
                              id="widgets.moveInCostBreakdown.valueCoverage"
                              defaultMessage="Value Coverage"
                            />
                          </a>
                        ),
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </Col>
        </div>
      </div>
    </div>
  );
};

StorageMartCheckoutConfirmation.propTypes = propTypes;
StorageMartCheckoutConfirmation.defaultProps = defaultProps;

export default StorageMartCheckoutConfirmation;
